import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, FileText, Mail, Upload, Download, Trash2, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import { EMAIL_TEMPLATES, fillPlaceholders } from "./emailTemplates";

const Caption = ({ children }) => (
  <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-2">{children}</div>
);

const enc = encodeURIComponent;
const FROM = process.env.REACT_APP_OUTREACH_EMAIL;
const CLIENTS = [
  { id: "gmail", label: "Gmail", url: (to, su, body) => `https://mail.google.com/mail/?authuser=${enc(FROM)}&view=cm&fs=1&to=${enc(to)}&su=${enc(su)}&body=${enc(body)}` },
  { id: "outlook", label: "Outlook", url: (to, su, body) => `https://outlook.office.com/mail/deeplink/compose?to=${enc(to)}&subject=${enc(su)}&body=${enc(body)}` },
  { id: "mailto", label: "App di posta", url: (to, su, body) => `mailto:${to}?subject=${enc(su)}&body=${enc(body)}` },
];

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const PdfPanel = ({ generated }) => {
  const [pdfs, setPdfs] = useState([]);
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  useEffect(() => { adminApi.listPdfs().then((d) => setPdfs(d.pdfs)).catch(() => {}); }, []);
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Seleziona un file PDF");
    if (file.size > 10 * 1024 * 1024) return toast.error("Il PDF supera i 10 MB");
    setProgress(0);
    try { const d = await adminApi.uploadPdf(file, setProgress); setPdfs((p) => [d, ...p]); toast.success("PDF caricato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setProgress(null); input.current.value = ""; }
  };
  const download = async (p) => { try { saveBlob(await adminApi.downloadPdf(p.id), p.filename); } catch { toast.error("Download non riuscito"); } };
  const remove = async (p) => { await adminApi.deletePdf(p.id); setPdfs((x) => x.filter((y) => y.id !== p.id)); };
  return (
    <div data-testid="pdf-panel" className="mt-4 border border-[#e4e4e7] bg-white p-3 text-xs space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-semibold flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#E0262C]" /> PDF da allegare</span>
        <button data-testid="pdf-upload-btn" onClick={() => input.current.click()} disabled={progress !== null}
          className="inline-flex items-center gap-1 border border-[#2B3036] px-2 py-1 hover:bg-[#2B3036] hover:text-white disabled:opacity-50">
          {progress !== null ? <><Loader2 className="w-3 h-3 animate-spin" /> {progress}%</> : <><Upload className="w-3 h-3" /> Carica PDF (max 10 MB)</>}
        </button>
        <input ref={input} data-testid="pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} />
      </div>
      {generated?.preview_url && (
        <a data-testid="pdf-generated-link" href={generated.preview_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 underline">
          <Download className="w-3 h-3" /> Presentazione generata allo Step 1
        </a>
      )}
      {pdfs.map((p) => (
        <div key={p.id} data-testid={`pdf-item-${p.id}`} className="flex items-center gap-2">
          <button onClick={() => download(p)} className="flex items-center gap-1.5 underline truncate text-left"><Download className="w-3 h-3 flex-shrink-0" />{p.filename}</button>
          <span className="text-[#6b7280] font-mono">{Math.round(p.size / 1024)} KB</span>
          <button data-testid={`pdf-delete-${p.id}`} onClick={() => remove(p)} className="ml-auto text-[#6b7280] hover:text-[#E0262C]"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      {!generated?.preview_url && !pdfs.length && <div className="text-[#6b7280]">Nessun PDF: caricane uno o generalo allo Step 1.</div>}
      <div className="text-[#a16207]">Scarica il PDF e allegalo nella tua email prima di inviarla.</div>
    </div>
  );
};

const LeadRow = ({ lead, subject, message, done, onOpen }) => (
  <div data-testid={`compose-row-${lead.id}`} className="flex flex-wrap items-center gap-2 px-6 py-2.5 border-b border-[#e9edef]">
    <div className="min-w-0 mr-auto">
      <div className="text-sm truncate">{lead.company_name}</div>
      <div className="text-[11px] font-mono text-[#6b7280] truncate">{lead.email || "nessuna email"}</div>
    </div>
    {done && <span className="text-[10px] font-mono text-[#10b981] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> aperta</span>}
    {CLIENTS.map((c) => (
      <a key={c.id} data-testid={`compose-${c.id}-${lead.id}`} href={lead.email ? c.url(lead.email, fillPlaceholders(subject, lead), fillPlaceholders(message, lead)) : undefined}
        target={c.id === "mailto" ? undefined : "_blank"} rel="noreferrer" onClick={() => lead.email && onOpen(lead)}
        className={`text-xs border px-3 h-8 inline-flex items-center gap-1.5 ${lead.email ? "border-[#d5dbde] hover:border-[#E0262C] text-[#2B3036]" : "border-[#e9edef] text-[#b0b7bc] pointer-events-none"}`}>
        <ExternalLink className="w-3 h-3" /> {c.label}
      </a>
    ))}
  </div>
);

export const EmailModal = ({ data, setData, onContacted, pdfData }) => {
  const first = data.leads[0];
  const [done, setDone] = useState({});
  const pickTemplate = (id) => {
    const t = EMAIL_TEMPLATES.find((x) => x.id === id);
    setData({ ...data, templateId: id, subject: t.subject, message: t.message });
  };
  const opened = async (lead) => {
    setDone((d) => ({ ...d, [lead.id]: true }));
    try { onContacted(await adminApi.markContacted(lead.id, { subject: fillPlaceholders(data.subject, lead), kind: data.templateId === "followup" ? "followup" : "manual" })); }
    catch { toast.error("Impossibile aggiornare il CRM"); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-6" onClick={() => setData(null)}>
      <div data-testid="email-modal" className="bg-white border border-[#d5dbde] max-w-4xl w-full flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#d5dbde] flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl">Prepara email</h3>
            <p className="text-xs text-[#6b7280] font-mono mt-0.5">Si apre nella casella <span className="text-[#2B3036]">{FROM}</span>: alleghi il PDF e invii tu · la guida per collegarla è in cima al tab Lead Gen</p>
          </div>
          <button data-testid="email-modal-close" onClick={() => setData(null)} className="text-[#6b7280] hover:text-[#E0262C]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 overflow-y-auto">
          <div className="p-6 space-y-4 lg:border-r border-[#d5dbde]">
            <div>
              <Caption>Template</Caption>
              <select data-testid="email-template-select" value={data.templateId} onChange={(e) => pickTemplate(e.target.value)}
                className="w-full h-11 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm px-3">
                {EMAIL_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Caption>Oggetto</Caption>
              <Input data-testid="email-subject-input" value={data.subject} onChange={(e) => setData({ ...data, subject: e.target.value })}
                className="h-11 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm" />
            </div>
            <div>
              <Caption>Messaggio</Caption>
              <textarea data-testid="email-message-input" value={data.message} rows={10} onChange={(e) => setData({ ...data, message: e.target.value })}
                className="w-full p-3 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm leading-relaxed" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {["{nome}", "{citta}", "{azienda}"].map((ph) => (
                  <button key={ph} data-testid={`insert-ph-${ph.slice(1, -1)}`} type="button" onClick={() => setData({ ...data, message: `${data.message}${ph}` })}
                    className="text-[11px] font-mono text-[#E0262C] border border-dashed border-[#d5dbde] hover:border-[#E0262C] px-2 py-0.5">+ {ph}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-6 bg-[#fafafa] text-[#09090b]">
            <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-3">Anteprima · {first.company_name}</div>
            <div data-testid="email-preview" className="bg-white border border-[#e4e4e7]">
              <div className="px-5 py-3 border-b border-[#e4e4e7] text-sm font-semibold">{fillPlaceholders(data.subject, first)}</div>
              <div className="px-5 py-4 text-sm leading-relaxed whitespace-pre-line">{fillPlaceholders(data.message, first)}</div>
            </div>
            <PdfPanel generated={pdfData} />
          </div>
        </div>
        <div className="border-t border-[#d5dbde] max-h-56 overflow-y-auto">
          <div className="px-6 pt-3 pb-1"><Caption><Mail className="w-3 h-3 inline mr-1" />Apri nella tua email · {data.leads.length} destinatari</Caption></div>
          {data.leads.map((l) => <LeadRow key={l.id} lead={l} subject={data.subject} message={data.message} done={done[l.id]} onOpen={opened} />)}
        </div>
      </div>
    </div>
  );
};
